import "./polyfills";
// CSSの優先度が変わってしまうので、必ずRoutesより前でimportする
import "@studyplus/boron-ui/dist/boron-ui.css";
import { createRoot } from "react-dom/client";
import ReactModal from "react-modal";
import { BrowserRouter as Router } from "react-router-dom";
import type {} from "redux-thunk/extend-redux";
import Routes from "./containers/Routes";
import "./styles/global.scss";
import "./styles/tailwind.css";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ReactGA4 from "react-ga4";
import { Helmet } from "react-helmet";
import Settings from "./helpers/Settings";
import store, { persistor } from "./store";

declare global {
  /**
   * NOTE: windowオブジェクトの拡張。
   * どうしても必要なものはここへ。極力windowへのアサインは避けてください
   */
  interface Window {
    // windowがスクロール領域ではないため、MainLayoutのonScrollコールバックを
    // 外のコンポーネントから渡すため、windowに生やしています
    onMainScrollAreaScroll?: () => void;

    // 環境変数に依存した設定値
    // 設定値は別のビルドで生成したJS(src/environment.js)からwindowに生やしているため、
    // そこを参照できるようにしています
    Environment: { [key: string]: any };
  }
}

const gaUrl = "https://www.google-analytics.com/g/collect";
if (Settings.SENTRY_DSN) {
  Sentry.init({
    dsn: Settings.SENTRY_DSN,
    environment: Settings.ENVIRONMENT,
    release: Settings.RELEASE,
    enabled: Settings.ENVIRONMENT !== "local",
    ignoreErrors: [
      "TypeError: Failed to fetch",
      "TypeError: NetworkError when attempting to fetch resource.",
      "Can't find variable: IntersectionObserver",
      "Can't find variable: ResizeObserver",
      "Can't find variable: gmo",
      "Non-Error promise rejection captured with keys",

      // google translate extension由来と思われるエラーを回避
      // 部分一致なのでevaluating 'a.fa'やevaluating 'a.h'などこれまで出たことのあるエラーを回避することを期待している
      "undefined is not an object (evaluating 'a.",
      // 特定のユーザーでのみ起きているエラーを無視
      // ユーザーに確認したところ画面の利用には影響ないそうでした
      // また、特別な拡張などは入れていないとのことでしたが、
      // boron-webからfacebookのAPIを叩いたりなどもしていないので、ユーザー環境固有の問題だと判断して無視しています
      // ref: https://studyplus.slack.com/archives/CNXH12CVD/p1689729571938059
      "Cannot read properties of undefined (reading 'facebook.json')",
      // 画面遷移を素早く行うとunseen_countのリクエストがキャンセルされて頻発するエラー。ユーザー影響がないエラーなので無視して問題ない。
      "AbortError: signal is aborted without reason",
      // SafariにおけるTypeError: Failed to fetchの同等エラー。Chrome/Firefoxの同等エラーと同様に無視する。
      "TypeError: Load failed",
      // iOS Safari拡張機能(WebExtension API)由来のエラー。boron-webからruntime.sendMessageを呼ぶ箇所はなく、
      // ユーザー環境の拡張機能起因のノイズなので無視する。
      // ref: https://studyplus.sentry.io/issues/BORON-WEB-1EQ
      "Invalid call to runtime.sendMessage()",
    ],
    allowUrls: [Settings.ROOT_URL],
    beforeSend(event) {
      const exception = event.exception;

      if (exception && exception.values) {
        // NOTE: boron-studentと同じく、google-analyticsへのリクエストのエラーを無視する
        // TypeError: Load failedのエラーか確認する
        if (
          exception.values[0].type === "TypeError" &&
          exception.values[0].value === "Load failed"
        ) {
          // google-analyticsにリクエストされているか確認
          const breadcrumbs = event.breadcrumbs;
          if (breadcrumbs) {
            const hasGoogleAnalyticsError = breadcrumbs.some(
              (breadcrumb) =>
                breadcrumb.type === "http" &&
                breadcrumb.level === "error" &&
                breadcrumb.data?.url.startsWith(gaUrl),
            );

            if (hasGoogleAnalyticsError) {
              return null;
            }
          }
        }
      }
      return event;
    },
  });
}

if (Settings.GOOGLE_ANALYTICS_MESUREMENT_ID) {
  ReactGA4.initialize(Settings.GOOGLE_ANALYTICS_MESUREMENT_ID);
}

// CSSからwindow高さを参照するためにdocumentからCSS変数へセットしている
// ref: https://coliss.com/articles/build-websites/operation/css/viewport-units-on-mobile.html
document.documentElement.style.setProperty(
  "--innerHeight",
  `${window.innerHeight}px`,
);

const queryClient = new QueryClient();

const renderApp = () => {
  const container = document.getElementById("root");
  if (container !== null) {
    // react-modal のアクセシビリティ設定を createRoot の前に明示的に行う。
    // http://reactcommunity.org/react-modal/accessibility/
    if (window.Environment?.MODE !== "test") {
      ReactModal.setAppElement(container);
    }
    // React 19 で追加された createRoot オプションに Sentry の error handler を接続する。
    // React 19 は Error Boundary で捕捉されたエラーの console.error 出力を抑制する破壊的変更が入っており、
    // これらのハンドラを設定しないと Sentry.ErrorBoundary の外側で起きたエラーが Sentry に届かなくなる可能性がある。
    // https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/
    //
    // onCaughtError は接続しない (二重送信回避のため):
    // 各 ErrorBoundary が自身で Sentry 送信責任を負う設計。
    // - FSErrorBoundary (Sentry.ErrorBoundary): Sentry.ErrorBoundary が componentDidCatch 内で自動送信
    // - ABTestSwitcher 内部の独自 ErrorBoundary: componentDidCatch で Sentry.captureReactException を呼び出し
    // ここで onCaughtError を接続すると、上記の各 ErrorBoundary が捕捉したエラーが Sentry に二重送信されてしまう。
    // 今後 Sentry 非対応の独自 ErrorBoundary を追加する場合は、内部で Sentry 送信を行うか、
    // 本コメントの前提を再検討すること。
    const root = createRoot(container, {
      onUncaughtError: Sentry.reactErrorHandler(),
      onRecoverableError: Sentry.reactErrorHandler(),
    });
    root.render(
      <>
        <Helmet>
          {Settings.ENVIRONMENT !== "prod" && (
            <meta name="robots" content="noindex"></meta>
          )}
        </Helmet>
        <QueryClientProvider client={queryClient}>
          <Router>
            <Routes store={store} persistor={persistor} />
          </Router>
          {window.Environment.MODE === "development" ? (
            <ReactQueryDevtools initialIsOpen={false} />
          ) : null}
        </QueryClientProvider>
      </>,
    );
  }
};

// 開発時、MSW_BROWSERをenableにするとモックサーバーを有効にできます
// デザインレビューでも使えるようにcage環境でも有効化できるようにしています
// モックの中身はmock/browser.tsにあります
// 注: 起動直後の bootstrap リクエスト（/me 等）の取りこぼしを防ぐため、worker.start() の
//     完了を待ってから描画する。
if (
  (Settings.ENVIRONMENT === "local" || Settings.ENVIRONMENT === "cage") &&
  Settings.MSW_BROWSER === "enable"
) {
  const { worker } = require("../mock/browser");
  worker.start({ onUnhandledRequest: "bypass" }).then(renderApp);
} else {
  renderApp();
}
